import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { FieldArray } from 'formik'

import { getNewResponsibles, getNewCollaborator } from '../../enhancers/definitions/people'
import { FormattedMessage } from '@dragonraider5/react-intl'
import { Link } from 'react-router-dom'
import { showFieldError, renderValidation } from '../../../../../utils/validations'
import { getExceptionMessage } from '../../../../../utils/exceptions'
import Button from '../../../../../components/Button'
import Fieldset from '../../../../../components/FormFields/Fieldset'
import { SectionSubTitle } from '../../../../../components/Section'
import FieldList, { FieldListHeader, FieldListItem, FieldListField, RemoveButton, AddButton } from '../../../../../components/FormFields/FieldList'
import FormInfo from '../../../../../components/FormFields/FormInfo'
import FormActions from '../../../../../components/FormFields/FormAction/FormActions'
import FormError from '../../../../../components/FormFields/FormError'
import Input from '../../../../../components/FormFields/Input';

import { ReactComponent as AddIcon } from '../../../../../components/Icons/add-user-icon.svg'

import { getApplyToStepPath } from '../../../../MainRoutes/utils'
import { stepStatuses } from '../../../constants'
import { StatusSteps } from '../../StatusSteps'

function SolicitudeForm({
  values,
  status = {},
  errors,
  touched,
  isSubmitting,
  handleChange,
  handleBlur,
  handleSubmit,
  application
}) {
  return (
    <form onSubmit={handleSubmit}>
      <Fieldset>
        <SectionSubTitle as='legend'>Responsables del proyecto dentro de la organización</SectionSubTitle>
        <FieldList>
          <FieldListHeader>
            <FieldListField as='span' flex='6'>Nombre</FieldListField>
            <FieldListField as='span' flex='6'>Años de experiencia</FieldListField>
            <FieldListField as='span' flex='6'>Años en la empresa</FieldListField>
          </FieldListHeader>
          <FieldArray
            name='responsibles'
            render={helpers => (
              <Fragment>
                {values.responsibles.map((responsible, index) => (
                  <FieldListItem key={responsible.id || index}>
                    <FieldListField
                      flex='6'
                      error={showFieldError(`responsibles[${index}].name`, { errors, touched })}
                      help={renderValidation(`responsibles[${index}].name`, { errors, touched })}
                    >
                      <Input
                        name={`responsibles[${index}].name`}
                        placeholder='Nombre'
                        value={responsible.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={showFieldError(`responsibles[${index}].name`, { errors, touched })}
                        full
                      />
                    </FieldListField>
                    <FieldListField
                      flex='6'
                      error={showFieldError(`responsibles[${index}].experience`, { errors, touched })}
                      help={renderValidation(`responsibles[${index}].experience`, { errors, touched })}
                    >
                      <Input
                        name={`responsibles[${index}].experience`}
                        placeholder='Años de experiencia'
                        type='number'
                        value={responsible.experience}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={showFieldError(`responsibles[${index}].experience`, { errors, touched })}
                        min={0}
                        full
                      />
                    </FieldListField>
                    <FieldListField
                      flex='6'
                      error={showFieldError(`responsibles[${index}].years_in`, { errors, touched })}
                      help={renderValidation(`responsibles[${index}].years_in`, { errors, touched })}
                    >
                      <Input
                        name={`responsibles[${index}].years_in`}
                        placeholder='Años en la empresa'
                        type='number'
                        value={responsible.years_in}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={showFieldError(`responsibles[${index}].years_in`, { errors, touched })}
                        min={0}
                        full
                      />
                    </FieldListField>
                    <FieldListField
                      flex='1'
                    >
                      {index > 0 && (
                        <RemoveButton title='Eliminar' onClick={() => helpers.remove(index)} />
                      )}
                    </FieldListField>
                  </FieldListItem>
                ))}
                <AddButton onClick={() => helpers.push(getNewResponsibles())}>
                  Añadir <AddIcon />
                </AddButton>
              </Fragment>
            )}
          />
        </FieldList>
      </Fieldset>
      <Fieldset>
        <SectionSubTitle as='legend'>Colaboradores/as</SectionSubTitle>
        <FormInfo>Por favor, indica si existen otras personas colaboradoras dentro del proyecto, a fin de poder hacer un mejor estudio del mismo</FormInfo>
        <FieldList>
          <FieldListHeader>
            <FieldListField flex='6' as='span'>Nombre</FieldListField>
            <FieldListField flex='6' as='span'>Participación en el proyecto</FieldListField>
            <FieldListField flex='1' />
          </FieldListHeader>
          <FieldArray
            name='collaborators'
            render={helpers => (
              <Fragment>
                {values.collaborators.map((collaborator, index) => (
                  <FieldListItem key={collaborator.id || index}>
                    <FieldListField
                      flex='6'
                      error={showFieldError(`collaborators[${index}].name`, { errors, touched })}
                      help={renderValidation(`collaborators[${index}].name`, { errors, touched })}
                    >
                      <Input
                        name={`collaborators[${index}].name`}
                        placeholder='Nombre'
                        value={collaborator.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={showFieldError(`collaborators[${index}].name`, { errors, touched })}
                        full
                      />
                    </FieldListField>
                    <FieldListField
                      flex='6'
                      error={showFieldError(`collaborators[${index}].how_participates`, { errors, touched })}
                      help={renderValidation(`collaborators[${index}].how_participates`, { errors, touched })}
                    >
                      <Input
                        name={`collaborators[${index}].how_participates`}
                        placeholder='¿Cómo participa?'
                        value={collaborator.how_participates}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={showFieldError(`collaborators[${index}].how_participates`, { errors, touched })}
                        full
                      />
                    </FieldListField>
                    <FieldListField
                      flex='1'
                    >
                      <RemoveButton title='Eliminar' onClick={() => helpers.remove(index)} />
                    </FieldListField>
                  </FieldListItem>
                ))}
                <AddButton
                  onClick={() => helpers.push(getNewCollaborator())}
                >
                  Añadir <AddIcon />
                </AddButton>
              </Fragment>
            )}
          />
        </FieldList>
      </Fieldset>
      {status.error && (
        <FormError>
          <FormattedMessage {...getExceptionMessage(status.error)} />
        </FormError>
      )}
      <FormActions>
        <Button type='submit' disabled={isSubmitting}>Continuar</Button>
        <Link to={getApplyToStepPath(stepStatuses.NEEDS, application)}>Volver</Link>
      </FormActions>
      <StatusSteps />
    </form>
  )
}

SolicitudeForm.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  status: PropTypes.shape({
    error: PropTypes.instanceOf(Error),
    success: PropTypes.object
  }),
  isSubmitting: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired
}

export default SolicitudeForm
